/**
 * Validates a password according to the following rules:
 * - Contains at least one letter
 * - Contains at least one number
 * - Is at least 12 characters and less than 50 characters
 * - Accepts printable ASCII characters (0x20-0x7E)
 * - Is case sensitive
 * - Cannot contain the term "password"
 * @param {string} password
 * @returns {{
 *   hasLetter: boolean,
 *   hasNumber: boolean,
 *   isLongEnough: boolean,
 *   isShortEnough: boolean,
 *   isAscii: boolean,
 *   noPasswordWord: boolean,
 *   valid: boolean
 * }} Validation results
 */
export function validatePassword(password) {
  const asciiRegex = /^[\x20-\x7E]+$/;
  const hasLetter = /[A-Za-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const isLongEnough = password.length >= 12;
  const isShortEnough = password.length < 50;
  const isAscii = asciiRegex.test(password);
  const noPasswordWord = !/password/i.test(password);

  return {
    hasLetter,
    hasNumber,
    isLongEnough,
    isShortEnough,
    isAscii,
    noPasswordWord,
    valid:
      hasLetter
      && hasNumber
      && isLongEnough
      && isShortEnough
      && isAscii
      && noPasswordWord,
  };
}

/**
 * Checks if two passwords match and are non-empty.
 * @param {string} originalPassword
 * @param {string} confirmPassword
 * @returns {boolean}
 */
export function passwordsMatchCheck(originalPassword, confirmPassword) {
  return originalPassword === confirmPassword && confirmPassword.length > 0;
}

/**
 * Helper to create a requirement checker.
 * @param {(password: string) => boolean} checker
 * @param {string} message - The error message to return if the check fails.
 * @returns {(password: string) => string|undefined}
 */
function checkPassword(checker, message) {
  return (password = '') => {
    if (!checker(password)) {
      return message;
    }
    return undefined;
  };
}

/**
 * A configuration object that defines a single password requirement and provides a way
 * to check that the requirement is fulfilled.
 *
 * @typedef {object} PasswordRequirement
 * @property {string} shortDescription - A brief description of the requirement.
 * @property {(password: string) => string | undefined} validator - A function that checks if the password meets the requirement.  The function should return the error message as a string if invalid, or undefined otherwise
 */

/** @type {PasswordRequirement[]} */
export const passwordRequirements = [
  {
    shortDescription: 'At least 12 characters',
    validator: checkPassword(
      (password) => validatePassword(password).isLongEnough,
      'Minimum 12 Characters',
    ),
  },
  {
    shortDescription: 'Less than 50 characters',
    validator: checkPassword(
      (password) => validatePassword(password).isShortEnough,
      'Password must be less than 50 characters.',
    ),
  },
  {
    shortDescription: 'Contains a letter',
    validator: checkPassword(
      (password) => validatePassword(password).hasLetter,
      'Contains a Letter',
    ),
  },
  {
    shortDescription: 'Contains a number',
    validator: checkPassword(
      (password) => validatePassword(password).hasNumber,
      'Contains a Number',
    ),
  },
  {
    shortDescription: 'Only printable ASCII characters',
    validator: checkPassword(
      (password) => password.length === 0 || validatePassword(password).isAscii,
      'Only Printable ASCII characters allowed.',
    ),
  },
  {
    shortDescription: 'Cannot contain the word "password"',
    validator: checkPassword(
      (password) => validatePassword(password).noPasswordWord,
      'Password cannot contain the word "password".',
    ),
  },
];
